import React, { useEffect, useMemo, useRef, useState } from 'react';

import { LuCalendarX, LuRotateCcw, LuScale, LuThumbsDown } from 'react-icons/lu';



const EMPTY_TIMINGS = [];

const normalise = word => word.toLowerCase().replace(/[’‘]/g, "'").replace(/[^\p{L}\p{N}']/gu, '');

// Keep measured speech cues; estimate only script tokens absent from the ASR.

function buildTimeline(narration, wordTimings, duration) {

  const tokens = [...narration.matchAll(/(\S+)(\s*)/g)];

  const script = tokens.map(token=>token[1]);

  // Estimate speech and pauses separately when measured words are unavailable.

  // Paragraph breaks give each narrated detail time to remain highlighted.

  const speechSeconds = .4;

  const pauses = tokens.map(([_,word,space])=>

    (/[.!?]$/.test(word)?.35:/[,;:]$/.test(word)?.12:0) + (/\n\s*\n/.test(space)?.65:0));

  const weights = pauses.map(pause=>speechSeconds+pause);

  const spoken = (Array.isArray(wordTimings) ? wordTimings : []).filter(item =>

    item && typeof item.word === 'string' && Number.isFinite(item.start) && Number.isFinite(item.end) && item.start >= 0 && item.end >= item.start);

  const length = duration > 0 ? duration : spoken.length ? spoken[spoken.length - 1].end : weights.reduce((sum,weight)=>sum+weight,0);

  const mapped = Array(script.length).fill(null);

  if (spoken.length) {

    const rows = script.length + 1, columns = spoken.length + 1;

    const scores = new Uint16Array(rows * columns);

    const source = script.map(normalise), audio = spoken.map(item => normalise(item.word));

    for (let i = 1; i < rows; i += 1) for (let j = 1; j < columns; j += 1) {

      scores[i * columns + j] = source[i - 1] && source[i - 1] === audio[j - 1]

        ? scores[(i - 1) * columns + j - 1] + 1

        : Math.max(scores[(i - 1) * columns + j], scores[i * columns + j - 1]);

    }

    let i = script.length, j = spoken.length;

    while (i > 0 && j > 0) {

      if (source[i - 1] && source[i - 1] === audio[j - 1]) {

        mapped[i - 1] = { ...spoken[j - 1], measured: true }; i -= 1; j -= 1;

      } else if (scores[(i - 1) * columns + j] >= scores[i * columns + j - 1]) i -= 1;

      else j -= 1;

    }

  }

  const measured = mapped.filter(Boolean).length;

  for (let index = 0; index < mapped.length;) {

    if (mapped[index]) { index += 1; continue; }

    const from = index;

    while (index < mapped.length && !mapped[index]) index += 1;

    const start = from ? mapped[from - 1].end : 0;

    const end = Math.max(start, index < mapped.length ? mapped[index].start : length);

    const totalWeight = weights.slice(from,index).reduce((sum,weight)=>sum+weight,0);

    const scale = totalWeight ? (end-start)/totalWeight : 0;

    let cursor = start;

    for (let k = from; k < index; k += 1) {

      mapped[k] = {start:cursor,end:cursor+speechSeconds*scale,measured:false};

      cursor += weights[k]*scale;

    }

  }

  return { words: mapped, mode: measured === script.length ? 'measured' : measured ? 'mixed' : 'estimated' };

}

function wordOffset(narration, phrase) {

  const at = narration.indexOf(phrase);

  if (at < 0) throw new Error(`Missing narration cue: ${phrase}`);

  return narration.slice(0, at).trim().split(/\s+/).filter(Boolean).length;

}

function cueTime(narration, timeline, phrase) {

  return timeline.words[wordOffset(narration, phrase)]?.start ?? Infinity;

}

function activeIndex(items, now) {

  let index = 0;

  items.forEach((item, i) => { if (now >= item.start) index = i; });

  return index;

}

// Keep the same timing-input aliases and stable timeline as What Is Tribe.

function useNarrationTimeline(narration, wordTimings, duration) {

  const timingKey = JSON.stringify(Array.isArray(wordTimings)

    ? wordTimings.map(item => [item?.word, item?.start, item?.end]) : []);

  return useMemo(() => buildTimeline(narration, wordTimings, duration), [narration, duration, timingKey]);

}

// Read the audio clock each frame. Typing adds character boundaries so it

// stays in sync with speech, pause, seeking, and playback rate changes.

function useLessonClock({ time, audioElement, audioRef, playbackId, audioStatus } = {}, timeline) {

  const boundaries = useMemo(() => [...timeline.words.map(word => word.start), ...(timeline.animationBoundaries ?? [])].sort((a,b)=>a-b), [timeline]);

  const sourceTime = Number.isFinite(time) ? Math.max(0, time) : 0;

  const parentAnchor = useRef({ time: sourceTime, stamp: null });

  const controller = useRef({ time: sourceTime, boundary: -2, playing: false, rate: 1, syncId: 0 });

  const [clock, setClock] = useState({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1, syncId: 0, seeking: false });

  const boundaryAt = current => {

    let low = 0, high = boundaries.length;

    while (low < high) {

      const middle = (low + high) >>> 1;

      if (boundaries[middle] <= current + .005) low = middle + 1;

      else high = middle;

    }

    return low - 1;

  };

  useEffect(() => {

    const previous = parentAnchor.current;

    const stamp = performance.now();

    const elapsed = previous.stamp == null ? 0 : (stamp - previous.stamp) / 1000;

    const expected = previous.time + (controller.current.playing ? elapsed * controller.current.rate : 0);

    const seek = previous.stamp != null && Math.abs(sourceTime - expected) > .6;

    parentAnchor.current = { time: sourceTime, stamp };

    // The media element is authoritative when available; otherwise changes to

    // the parent's time keep the interpolated fallback clock anchored.

    if (!(audioElement ?? audioRef?.current) && seek) {

      controller.current.syncId += 1;

      controller.current.boundary = -2;

      controller.current.time = sourceTime;

      setClock({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1,

        syncId: controller.current.syncId, seeking: true });

    }

  }, [sourceTime, audioElement, audioRef]);

  useEffect(() => {

    const media = audioElement ?? audioRef?.current;

    const playbackChanged = controller.current.playbackId !== playbackId;

    if (!media) {

      const stamp = performance.now();

      const anchor = parentAnchor.current;

      const heldTime = anchor.time + (controller.current.playing && anchor.stamp != null

        ? (stamp-anchor.stamp)/1000*controller.current.rate : 0);

      // Anchor pause/resume to the elapsed clock instead of resetting to zero.

      parentAnchor.current = {time:playbackChanged?sourceTime:heldTime,stamp};

    }

    let frame;

    const read = () => {

      const state = controller.current;

      const playing = media ? !media.paused && !media.ended : audioStatus === 'Playing';

      const rate = media ? media.playbackRate || 1 : 1;

      const anchor = parentAnchor.current;

      const current = media ? Math.max(0, media.currentTime || 0) : Math.max(0,

        anchor.time + (playing && anchor.stamp != null ? (performance.now() - anchor.stamp) / 1000 * rate : 0));

      const boundary = boundaryAt(current);

      return { current, boundary, playing, rate };

    };

    const update = (force = false, seek = false) => {

      const sample = read(), state = controller.current;

      const rateChanged = sample.rate !== state.rate;

      if (seek || rateChanged) state.syncId += 1;

      state.time = sample.current;

      if (force || seek || rateChanged || sample.boundary !== state.boundary || sample.playing !== state.playing) {

        state.boundary = sample.boundary;

        state.playing = sample.playing;

        state.rate = sample.rate;

        setClock({ now: sample.current, playing: sample.playing, rate: sample.rate,

          syncId: state.syncId, seeking: seek || rateChanged });

      }

      return sample.playing;

    };

    const tick = () => { if (update()) frame = requestAnimationFrame(tick); };

    const play = () => { cancelAnimationFrame(frame); update(true); frame = requestAnimationFrame(tick); };

    const stop = () => { cancelAnimationFrame(frame); update(true); };

    const seek = () => { update(true, true); };

    const rate = () => { update(true); };

    const sync = () => { update(); };

    if (media) {

      media.addEventListener('play', play); media.addEventListener('pause', stop);

      media.addEventListener('ended', stop); media.addEventListener('seeked', seek);

      media.addEventListener('ratechange', rate); media.addEventListener('timeupdate', sync);

    }

    if (controller.current.playbackId !== playbackId) {

      controller.current.playbackId = playbackId;

      controller.current.syncId += 1;

    }

    update(true);

    if (read().playing) frame = requestAnimationFrame(tick);

    return () => {

      cancelAnimationFrame(frame);

      if (media) {

        media.removeEventListener('play', play); media.removeEventListener('pause', stop);

        media.removeEventListener('ended', stop); media.removeEventListener('seeked', seek);

        media.removeEventListener('ratechange', rate); media.removeEventListener('timeupdate', sync);

      }

    };

  }, [audioElement, audioRef, playbackId, audioStatus, boundaries]);

  return clock;

}

// Emphasis is presentation-only. Match whole words without depending on

// capitalization or punctuation; an absent emphasis phrase is simply skipped.

function emphasisIndexes(words, emphasis) {

  const source = words.map(normalise);

  const strong = new Set();

  for (const phrase of Array.isArray(emphasis) ? emphasis : []) {

    if (typeof phrase !== 'string') continue;

    const target = phrase.split(/\s+/).filter(Boolean).map(normalise).filter(Boolean);

    if (!target.length) continue;

    const offset = source.findIndex((_, index) => target.every((word, i) => source[index + i] === word));

    if (offset < 0) continue;

    target.forEach((_, index) => strong.add(offset + index));

  }

  return strong;

}



// Phrase-cued motion matches What Is Tribe and Event Structure. Delays stay

// fixed between clock updates, so CSS completes the gentle rise smoothly.

function PhraseReveal({ as: Tag = 'h1', text, emphasis = [], clock, complete = false, delay = 0 }) {

  const words = text.split(/\s+/).filter(Boolean);

  const settled = useMemo(() => clock.seeking || (!clock.playing && clock.now > 0), [clock.syncId, text]);

  const strong = emphasisIndexes(words, emphasis);

  return <Tag aria-label={text}>{words.map((word, index) => <React.Fragment key={index}>

    <span className={`expectations-reveal-word ${strong.has(index) ? 'is-emphasized' : ''}`} aria-hidden="true" style={{

      animationDelay: `${complete || settled ? -10 : (delay + index * .09) / clock.rate}s`,

      animationDuration: `${.38 / clock.rate}s`,

      animationPlayState: complete || clock.playing ? 'running' : 'paused',

    }}>{word}</span>{index < words.length - 1 ? ' ' : ''}

  </React.Fragment>)}</Tag>;

}









const EXPECTATION_POINTS = [

  {cue:'The first violation', icon:LuCalendarX, title:'Canceling an Event', description:'Canceling less than 48 hours before the start time, unless there’s a real emergency.'},

  {cue:'The second violation', icon:LuRotateCcw, title:'Repeated Cancellations', description:'Repeated cancellations or last-minute changes, even outside the 48-hour window.'},

  {cue:'The third violation', icon:LuScale, title:'Misconduct', description:'Harassment, discrimination, abusive language, or unwanted physical contact.'},

  {cue:'The last violation', icon:LuThumbsDown, title:'Bad Hosting Quality', description:'Consistently showing up unprepared or making little effort to engage guests.'},

];

const INTRO_SCENES = [

  {cue:'The final area', text:'The final area we’ll cover today is operations.', emphasis:['operations']},

  {cue:'specifically how payment works', text:'Payment and host expectations.', emphasis:['Payment','host expectations']},

  {cue:'Let’s be clear', text:'Let’s be clear about what we expect from you as a host.', emphasis:['expect from you']},

  {cue:'There are a few things', text:'Your hosting status depends on it.', emphasis:['hosting status']},

];

function buildExpectationCues(narration,timeline) {

  const points=EXPECTATION_POINTS.map(point=>({...point,start:cueTime(narration,timeline,point.cue)}));

  const intro=INTRO_SCENES.map(scene=>({...scene,start:cueTime(narration,timeline,scene.cue)}));

  return {points,intro,listAt:points[0].start,reviewAt:timeline.words[timeline.words.length-1]?.end??Infinity};

}

function ExpectationPoints({cues,clock,complete}) {

  const latest=complete?cues.points.length-1:cues.points.reduce((index,point,i)=>clock.now>=point.start?i:index,-1);

  const review=complete||clock.now>=cues.reviewAt;

  return <ol className={`expectations-field-items ${review?'is-reviewing':''}`} aria-label="Host expectations">

    {cues.points.map((point,index)=>{

      const current=!review&&index===latest;

      const Icon=point.icon;

      return <li key={point.cue} className={`expectations-field-item ${current?'is-current':''}`} aria-current={current?'step':undefined}>

        <div className="expectations-point-inner"><span className="expectations-number" aria-hidden="true">{String(index+1).padStart(2,'0')}</span>

        <Icon className="expectations-point-icon" size={30} strokeWidth={1.5} aria-hidden="true" focusable="false" />

        <div className="expectations-point-copy">

          <h2>{point.title}</h2>

          <p>{point.description}</p>

        </div>

        </div>

      </li>;

    })}

  </ol>;

}

// This lesson owns its visuals and narration metadata.

export default function HostExpectations(props={}) {

  const narration=HostExpectations.parts[0].narration;

  const speechTimeline=useNarrationTimeline(narration,props.wordTimings??props.words??EMPTY_TIMINGS,props.duration??0);

  const cues=useMemo(()=>buildExpectationCues(narration,speechTimeline),[narration,speechTimeline]);

  const timeline=useMemo(()=>({...speechTimeline,animationBoundaries:[...cues.intro.map(scene=>scene.start),cues.reviewAt]}),[speechTimeline,cues]);

  const clock=useLessonClock({...props,playbackId:`${props.playbackId??0}:expectations`},timeline);

  const complete=props.audioStatus==='Complete'||Boolean((props.audioElement??props.audioRef?.current)?.ended);

  const listVisible=complete||clock.now>=cues.listAt;

  const scene=cues.intro[activeIndex(cues.intro,clock.now)];

  return <div className="host-background-slide host-expectations-slide" aria-label={HostExpectations.title}>

    <style>{EXPECTATIONS_STYLES}</style>

    <section className={`expectations-scene ${listVisible?'is-list':'is-caption'}`} data-scene={listVisible?'expectations':scene.cue} data-timing-mode={timeline.mode}>

      <div className={`expectations-content ${listVisible?'is-grid':''}`}>

        <div className="expectations-headline" key={`${listVisible?'list':scene.cue}-${clock.syncId}`}>

          <PhraseReveal text={listVisible?'Reasons we may remove a host':scene.text} emphasis={listVisible?[]:scene.emphasis} clock={clock} complete={complete} />

        </div>

        {listVisible&&<ExpectationPoints cues={cues} clock={clock} complete={complete} />}

      </div>

    </section>

  </div>;

}



const EXPECTATIONS_STYLES = `

@import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@700;900&display=swap');

.host-expectations-slide {width:100%;background:#fff;color:#111;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;-webkit-font-smoothing:antialiased;}

.host-expectations-slide * {box-sizing:border-box;}

.host-expectations-slide .expectations-scene {min-height:530px;padding:32px 36px;}

.host-expectations-slide .expectations-scene.is-caption {display:flex;align-items:center;justify-content:center;}

.host-expectations-slide .expectations-content {width:100%;max-width:1100px;margin:0 auto;}

.host-expectations-slide .expectations-headline {max-width:850px;margin:0 auto;text-align:center;}

.host-expectations-slide .expectations-headline h1 {margin:0;font-family:Merriweather,Georgia,serif;font-size:clamp(25px,2.6vw,34px);font-weight:700;line-height:1.2;letter-spacing:-.025em;}

.host-expectations-slide .is-caption .expectations-headline h1 {font-size:clamp(32px,4vw,50px);line-height:1.12;}

.host-expectations-slide .expectations-content.is-grid {max-width:900px;}

.host-expectations-slide .is-grid .expectations-headline {text-align:center;margin:0 auto 36px;padding:0;}

.host-expectations-slide .expectations-field-items {display:grid;grid-template-columns:repeat(2,minmax(0,1fr));list-style:none;width:100%;margin:0;padding:0;}

.host-expectations-slide .expectations-field-item {min-width:0;min-height:200px;padding:22px 32px;color:#111;}

.host-expectations-slide .expectations-field-item:nth-child(even) {border-left:1px solid #e5e5e5;}

.host-expectations-slide .expectations-field-item:nth-child(n+3) {border-top:1px solid #e5e5e5;}

.host-expectations-slide .expectations-point-inner {opacity:.38;transition:opacity 420ms ease;}

.host-expectations-slide .is-current .expectations-point-inner,.host-expectations-slide .is-reviewing .expectations-point-inner {opacity:1;}

.host-expectations-slide .expectations-number {display:grid;place-items:center;width:26px;height:26px;border:1px solid #d8d8d8;border-radius:50%;font-size:11px;font-weight:500;line-height:1;font-variant-numeric:tabular-nums;}

.host-expectations-slide .expectations-point-icon {display:block;margin:16px 0 0;color:currentColor;}

.host-expectations-slide .expectations-field-item h2 {margin:14px 0 9px;font-size:17px;font-weight:650;line-height:1.3;letter-spacing:-.022em;}

.host-expectations-slide .expectations-field-item p {margin:0;max-width:280px;color:currentColor;font-size:13px;font-weight:400;line-height:1.55;letter-spacing:-.014em;}

.host-expectations-slide .expectations-reveal-word {display:inline-block;opacity:0;transform:translateY(.36em);animation:expectations-word-in 380ms cubic-bezier(.18,.84,.22,1) both;}

.host-expectations-slide .expectations-reveal-word.is-emphasized {font-weight:900;}

@keyframes expectations-word-in {to{opacity:1;transform:translateY(0);}}

.host-training .ht-lesson-layout:has(.host-expectations-slide) {display:block;padding:0;}

.host-training .ht-lesson-layout:has(.host-expectations-slide)>.ht-lesson-copy {display:none;}

.host-training .ht-lesson-visual:has(.host-expectations-slide) {display:block;padding:0;background:#fff;border:0;border-radius:0;}

@media(max-width:800px){

 .host-expectations-slide .expectations-scene {padding:28px 24px;}

 .host-expectations-slide .expectations-content.is-grid {max-width:900px;}

 .host-expectations-slide .is-grid .expectations-headline {padding:0;margin-bottom:28px;max-width:100%;}

 .host-expectations-slide .expectations-field-items {border:0;}

 .host-expectations-slide .expectations-field-item {padding:22px 20px;}

 .host-expectations-slide .expectations-field-item:nth-child(odd) {padding-left:0;}

}

@media(max-width:480px){

 .host-expectations-slide .is-caption .expectations-headline h1 {font-size:30px;}

 .host-expectations-slide .expectations-field-items {grid-template-columns:1fr;}

 .host-expectations-slide .expectations-field-item {min-height:0;padding:22px 0;}

 .host-expectations-slide .expectations-field-item:nth-child(even) {border-left:0;}

 .host-expectations-slide .expectations-field-item:nth-child(n+2) {border-top:1px solid #e5e5e5;}

}

@media(prefers-reduced-motion:reduce){

 .host-expectations-slide .expectations-reveal-word {animation:none!important;opacity:1;transform:none;}

 .host-expectations-slide .expectations-point-inner {transition:none;}

}

`;



Object.assign(HostExpectations, {

  "slide": 12,

  "id": "expectations",

  "title": "Host Expectations",

  "section": "Operations",

  "parts": [

    {

      "part": 0,

      "narration": "The final area we’ll cover today is operations, specifically how payment works and our expectations for you as hosts.\n\nLet’s be clear about what we expect from you as a host.\nThere are a few things that can put your hosting status at risk.\n\nThe first violation is canceling less than 48 hours before an event. Unless there’s a real emergency, this isn’t okay. Guests plan around your event, and cancellations break trust.\n\nThe second violation is repeated cancellations or last-minute changes. Even if they’re technically outside the 48-hour window, a pattern tells us you’re not able to reliably run events.\n\nThe third violation is any form of misconduct. Harassment, discrimination, abusive language, or unwanted physical contact will lead to immediate removal as a host.\n\nThe last violation is consistently poor hosting. One difficult event can happen, but repeatedly showing up unprepared or making little effort to engage guests is not acceptable."

    }

  ],

  "summary": [

    "Be reliable, prepared, and engaged.",

    "Treat every guest with respect."

  ]

});
